import { ArrowUpRight, CalendarClock, ShieldCheck } from "lucide-react";
import { site } from "@/config";
import { FadeUp, MaskedLine, MonoLabel } from "@/components/Reveal";
import ParticleField from "@/components/ParticleField";

export default function CTA() {
  return (
    <section
      id="contact"
      className="relative overflow-hidden border-t border-black/10 px-6 sm:px-12 py-32 sm:py-44"
      data-testid="booking-section"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[70vw] h-[70vw] rounded-full bg-[#0071e3]/[0.08] blur-3xl animate-aurora" />
        <div className="absolute bottom-[-30%] -left-48 w-[45vw] h-[45vw] rounded-full bg-[#c644fc]/[0.06] blur-3xl animate-aurora-slow" />
        <ParticleField className="absolute inset-0 w-full h-full" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto text-center">
        <FadeUp>
          <MonoLabel>Let's talk</MonoLabel>
        </FadeUp>
        <h2 className="mt-6 font-display font-semibold text-5xl sm:text-6xl lg:text-7xl tracking-[-0.035em] leading-[1.03]">
          <MaskedLine>Ready to be</MaskedLine>
          <MaskedLine delay={0.1}>
            <span className="text-gradient">the answer?</span>
          </MaskedLine>
        </h2>
        <FadeUp delay={0.2}>
          <p className="mt-8 text-muted-foreground text-base sm:text-xl leading-relaxed max-w-xl mx-auto">
            Book a free 30 minute call. I'll ask about your pain points,
            what's bothering you, and your business metrics. By the end,
            we'll both know if this is a good fit.
          </p>
          <p className="mt-6 text-muted-foreground text-base leading-relaxed max-w-xl mx-auto">
            I only work with a handful of clients at a time, so each one gets
            the attention it deserves.
          </p>
        </FadeUp>
        <FadeUp delay={0.25} className="mt-8 flex justify-center">
          <div
            className="inline-flex items-center gap-3 rounded-full border border-black/10 bg-white/70 backdrop-blur px-6 py-3"
            data-testid="slots-badge"
          >
            <span className="w-2 h-2 rounded-full bg-[#30d158] animate-pulse-dot" />
            <span className="font-mono text-xs tracking-[0.15em] uppercase text-muted-foreground">
              2 slots open for Q4 2026
            </span>
          </div>
        </FadeUp>
        <FadeUp delay={0.3} className="mt-12">
          <a
            href={site.calendlyUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="calendly-book-btn"
            className="group inline-flex items-center gap-3 bg-primary text-primary-foreground rounded-full px-10 py-5 text-base font-medium hover:scale-[1.05] hover:shadow-[0_8px_30px_rgba(0,113,227,0.35)] transition-[transform,box-shadow] duration-300"
          >
            <CalendarClock className="w-4 h-4" />
            Book a discovery call
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </a>
          <p className="mt-6 font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground">
            Free · 30 min
          </p>
        </FadeUp>
        <FadeUp delay={0.4} className="mt-14 flex justify-center">
          <div
            className="inline-flex items-center gap-4 sm:gap-5 rounded-2xl border border-black/10 bg-white/70 backdrop-blur px-6 sm:px-8 py-5 text-left"
            data-testid="guarantee-badge"
          >
            <ShieldCheck className="w-9 h-9 text-primary shrink-0" />
            <div>
              <p className="font-display font-semibold text-base sm:text-lg tracking-[-0.01em]">
                The 8 month guarantee
              </p>
              <p className="text-muted-foreground text-sm sm:text-base mt-1 leading-relaxed">
                If I don't deliver what I promised in 8 months, I keep
                working for free until I do.
              </p>
            </div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}
